import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable, useServerTable } from "@dl/web-core-js/table";
import { Segmented } from "@dl/web-core-js/ui";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PART_GROUP_KINDS, StockBalanceClient, type PartGroupKind, type StockBalanceDTO } from "../../api/manufacturing";
import { NexpimView } from "../../views";
import { partGroupKindLabel } from "./partGroupKind";

type Sign = "POSITIVE" | "NEGATIVE";

/**
 * Stock still on the books for parts nobody uses any more: inactive parts with
 * a non-zero balance, one row per part and warehouse.
 *
 * <p>Two lists, because they ask different questions. A negative balance is
 * data to clean up. A positive one is material we hold and cannot use: what to
 * do with it (sell it, scrap it, use it up) is decided outside nexpim, this only
 * finds it.</p>
 */
export function InactiveStockPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const navigate = useNavigate();
  const allowed = hasView(NexpimView.MANUFACTURING_STOCK);
  const balances = useMemo(() => new StockBalanceClient(http), [http]);
  const [sign, setSign] = useState<Sign>("POSITIVE");

  // The two fixed filters ride on every request; the table's own filters come on top.
  const fixed = { part_status: ["INACTIVE"], sign: [sign] };
  const table = useServerTable<StockBalanceDTO>({
    queryKey: ["manufacturing", "stock-balance", "inactive", sign],
    fetchPage: (request) => balances.find({ ...request, filters: { ...request.filters, ...fixed } }),
    fetchMatchingIds: (request) => balances.findMatchingIds({ ...request, filters: { ...request.filters, ...fixed } }),
    enabled: allowed,
  });

  const cols = useMemo(() => {
    const c = defineColumns<StockBalanceDTO>();
    return [
      c.text("part_number", (row) => row.part_number, { headerKey: "column.part.number", priority: 1 }),
      c.text("part_name", (row) => row.part_name, { headerKey: "column.name", priority: 1 }),
      c.text("part_group_code", (row) => (row.part_group_code ? `${row.part_group_code} · ${row.part_group_name ?? ""}` : ""), {
        headerKey: "column.part.group",
        filter: "none",
        priority: 2,
      }),
      c.text("part_group_kind", (row) => (row.part_group_kind ? partGroupKindLabel(row.part_group_kind, t) : ""), {
        header: t("config.group-kind", "Kind"),
        filter: "multiselect",
        filterOptions: PART_GROUP_KINDS.map((kind: PartGroupKind) => ({ value: kind, label: partGroupKindLabel(kind, t) })),
        priority: 3,
      }),
      c.text("warehouse_code", (row) => row.warehouse_code, { headerKey: "column.stock.warehouse", filter: "none", priority: 2 }),
      c.number("quantity", (row) => row.quantity, { headerKey: "column.stock.quantity", filter: "range", priority: 1 }),
      c.text("stock_unit_code", (row) => row.stock_unit_code ?? "", {
        header: t("inactive-stock.unit", "Unit"),
        filter: "none",
        enableSorting: false,
        priority: 3,
      }),
      c.text("part_inactivated_date", (row) => row.part_inactivated_date ?? "", {
        header: t("inactive-stock.inactivated", "Inactive since"),
        filter: "daterange",
        priority: 3,
      }),
      c.text("last_movement_date", (row) => row.last_movement_date ?? "", {
        header: t("inactive-stock.last-movement", "Last movement"),
        filter: "daterange",
        priority: 3,
      }),
    ];
  }, [t]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("inactive-stock.title", "Inactive parts with stock")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        parent={{ to: "/manufacturing/stock", label: t("navigation.item.stock", "Stock") }}
        title={t("inactive-stock.title", "Inactive parts with stock")}
        subtitle={
          sign === "POSITIVE"
            ? t("inactive-stock.positive.subtitle", "Material on hand that no product uses any more: what to do with it is decided outside nexpim.")
            : t("inactive-stock.negative.subtitle", "Below zero on parts nobody uses: data to clean up with an adjustment or a count.")
        }
        actions={
          <Segmented<Sign>
            value={sign}
            onChange={setSign}
            options={[
              { value: "POSITIVE", label: t("inactive-stock.positive", "Positive") },
              { value: "NEGATIVE", label: t("inactive-stock.negative", "Negative") },
            ]}
          />
        }
      />
      <DataTable<StockBalanceDTO>
        key={sign}
        data={table.rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey="/manufacturing/stock/inactive"
        loading={table.loading}
        server={table.mode}
        onRowClick={(row) =>
          void navigate({ to: "/manufacturing/parts/$partId", params: { partId: String(row.part_id) }, search: { tab: "stock" } })
        }
        emptyMessage={t("table.empty", "Nothing to show")}
      />
    </>
  );
}
